import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/GlobalProvider";
import { GoPerson } from "react-icons/go";
import { IoBagHandleOutline } from "react-icons/io5";
import { IoIosLogOut } from "react-icons/io";
import TheWinnerLogo1080p from "./csdv2Logo.svg";
import { USER_CURRENCY } from "../../hooks/APIHooks";
import { IoWalletOutline } from "react-icons/io5";
import BottomNavbar from "./BottomNavbar";

const Navbar = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isWalletDropdownOpen, setIsWalletDropdownOpen] = useState(false); // Separate state for wallet dropdown

  const dropdownRef = useRef(null); // Create a ref for the dropdown
  const [digitalCurrency, setDigitalCurrency] = useState(null);

  const handleLogout = () => {
    signOut();
    navigate("/");
  };

  useEffect(() => {
    if (user?.user_id) {
      fetch(USER_CURRENCY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.user_id }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) setDigitalCurrency(data.digital_currency);
          else console.error("Error:", data.message);
        })
        .catch((err) => console.error("Fetch error:", err));
    }
  }, [user]);

  // Toggle dropdown visibility for profile
  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
    setIsWalletDropdownOpen(false); // Close wallet dropdown when opening profile
  };

  // Toggle dropdown visibility for wallet
  const toggleWalletDropdown = () => {
    setIsWalletDropdownOpen(!isWalletDropdownOpen);
    setIsProfileDropdownOpen(false); // Close profile dropdown when opening wallet
  };

  // Close dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
        setIsWalletDropdownOpen(false); // Close wallet dropdown as well
      }
    };

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Clean up the event listener
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div className="sticky w-full z-[99] top-0 bg-primary shadow-md ">
      <div className="flex items-center justify-between p-2 ">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          <Link to="/" className=" items-center justify-center">
            <img
              src={TheWinnerLogo1080p}
              alt="csdlogo"
              className="cursor-pointer w-full h-12"
            />
          </Link>
        </div>

        {/* Right: User Profile, Cart, Help */}
        <div className="flex items-center space-x-2 text-secondary">
          <div
            className="relative cursor-pointer"
            onClick={toggleProfileDropdown}
          >
            <GoPerson size={25} />
            {isProfileDropdownOpen && (
              <div
                ref={dropdownRef} // Attach the ref to the dropdown
                className="absolute right-0 bg-primary border border-gray-300 shadow-lg rounded-lg w-60 p-4 z-50 max-h-[calc(100vh-80px)] overflow-auto"
              >
                <div className="text-center mb-3">
                  <p className="font-bold text-md">
                    Hello, {user?.name ? user.name : "Guest"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user
                      ? "Find all your details here"
                      : "To access your account"}
                  </p>
                </div>

                {user ? (
                  <div>
                    <Link
                      to="/dashboard"
                      className="flex items-center space-x-2 py-2 text-sm text-secondary  rounded-md mb-2 transition duration-300"
                    >
                      <GoPerson size={20} />
                      <span>Dashboard</span>
                    </Link>

                    {user.role === "admin" && (
                      <Link
                        to="/admin-dashboard"
                        className="flex items-center space-x-2 py-2 text-sm text-secondary rounded-md mb-2 transition duration-300"
                      >
                        <GoPerson size={20} />
                        <span>Admin Dashboard</span>
                      </Link>
                    )}
                  </div>
                ) : (
                  <Link
                    to="/"
                    className="flex items-center space-x-2 py-2 text-sm text-secondary  rounded-md mb-2 transition duration-300"
                  >
                    <IoBagHandleOutline
                      size={20}
                      className="hover-yellow-500 "
                    />
                    <span>Sign Up/Sign In</span>
                  </Link>
                )}

                {user && (
                  <button
                    onClick={handleLogout}
                    className="flex justify-center items-center space-x-2 w-full text-white py-2 rounded-md bg-red-600 hover:bg-red-700 transition duration-300"
                  >
                    <IoIosLogOut size={20} className="hover-yellow" />
                    <span>Logout</span>
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="relative">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={toggleWalletDropdown}
            >
              <IoWalletOutline size={25} className="text-secondary" />

              <span className="text-sm font-bold text-secondary">
                {digitalCurrency !== null ? digitalCurrency : "Loading..."}
              </span>
            </div>

            {user && isWalletDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-0 mt-2 bg-primary border border-gray-300 shadow-lg rounded-lg w-60 p-4 z-50 max-h-[calc(100vh-80px)] overflow-auto"
              >
                <Link
                  to="/withdraw"
                  className="pl-2 block text-sm text-secondary py-2 rounded-md hover:bg-secondary hover:text-white transition duration-300"
                >
                  Withdraw Now!
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNavbar />
    </div>
  );
};

export default Navbar;
